import React, { useEffect, useMemo, useRef, useState } from "react";
import WebFont from "webfontloader";
import { getBadgeTemplateTextFonts } from "../helpers/badgeTemplate";
import { renderBadgeTemplatePreview } from "../helpers/renderBadgeTemplate";
import { getTemplateWithFirstAttendee } from "../helpers/templatePreviewAttendee";

const EVENT_CARD_PREVIEW_MULTIPLIER = 0.65;
const TEMPLATE_PREVIEW_CACHE_LIMIT = 80;
const templatePreviewCache = new Map();
const templatePreviewRenderPromises = new Map();

const getTemplatePreviewCacheKey = ({ badgeSize, template, multiplier }) =>
  JSON.stringify({ badgeSize, template, multiplier });

const getCachedTemplatePreview = (cacheKey) => templatePreviewCache.get(cacheKey);

const cacheTemplatePreview = (cacheKey, previewUrl) => {
  if (!previewUrl) return;

  if (templatePreviewCache.has(cacheKey)) {
    templatePreviewCache.delete(cacheKey);
  }
  templatePreviewCache.set(cacheKey, previewUrl);

  while (templatePreviewCache.size > TEMPLATE_PREVIEW_CACHE_LIMIT) {
    templatePreviewCache.delete(templatePreviewCache.keys().next().value);
  }
};

const getOrRenderTemplatePreview = ({
  badgeSize,
  cacheKey,
  canvasElement,
  template,
}) => {
  const cachedPreviewUrl = getCachedTemplatePreview(cacheKey);
  if (cachedPreviewUrl) {
    return Promise.resolve(cachedPreviewUrl);
  }

  const pendingRender = templatePreviewRenderPromises.get(cacheKey);
  if (pendingRender) {
    return pendingRender;
  }

  const renderPromise = (async () => {
    await loadTemplateFonts(template);
    const previewUrl = await renderBadgeTemplatePreview({
      canvasElement,
      template,
      badgeSize,
      multiplier: EVENT_CARD_PREVIEW_MULTIPLIER,
    });
    cacheTemplatePreview(cacheKey, previewUrl);
    return previewUrl;
  })();

  templatePreviewRenderPromises.set(cacheKey, renderPromise);
  renderPromise.then(
    () => templatePreviewRenderPromises.delete(cacheKey),
    () => templatePreviewRenderPromises.delete(cacheKey)
  );

  return renderPromise;
};

const loadTemplateFonts = (template) =>
  new Promise((resolve) => {
    const templateFonts = getBadgeTemplateTextFonts(template);
    if (!templateFonts.length) {
      resolve();
      return;
    }

    let didFinish = false;
    const finish = () => {
      if (didFinish) return;
      didFinish = true;
      clearTimeout(timeout);
      resolve();
    };
    const timeout = setTimeout(finish, 4000);

    try {
      WebFont.load({
        google: {
          families: templateFonts,
        },
        active: finish,
        inactive: finish,
      });
    } catch {
      finish();
    }
  });

const EventTemplatePreview = ({
  badgeSize,
  spreadsheetData = null,
  template,
  title,
  useFirstAttendee = false,
}) => {
  const canvasHostRef = useRef(null);
  const previewTemplate = useMemo(
    () =>
      useFirstAttendee
        ? getTemplateWithFirstAttendee(template, spreadsheetData)
        : template,
    [spreadsheetData, template, useFirstAttendee]
  );
  const previewInputRef = useRef({ badgeSize, template: previewTemplate });
  const canRender = Boolean(previewTemplate && badgeSize);
  const previewKey = useMemo(
    () =>
      canRender
        ? getTemplatePreviewCacheKey({
            badgeSize,
            template: previewTemplate,
            multiplier: EVENT_CARD_PREVIEW_MULTIPLIER,
          })
        : "",
    [badgeSize, canRender, previewTemplate]
  );
  const cachedPreviewUrl = canRender ? getCachedTemplatePreview(previewKey) : "";
  const [previewState, setPreviewState] = useState(() => ({
    key: previewKey,
    url: cachedPreviewUrl || "",
  }));
  const [isRendering, setIsRendering] = useState(
    () => canRender && !cachedPreviewUrl
  );
  previewInputRef.current = { badgeSize, template: previewTemplate };
  const previewUrl =
    previewState.key === previewKey ? previewState.url : cachedPreviewUrl || "";

  useEffect(() => {
    const {
      badgeSize: currentBadgeSize,
      template: currentTemplate,
    } = previewInputRef.current;

    if (!currentTemplate || !currentBadgeSize || !canvasHostRef.current) {
      setPreviewState({ key: previewKey, url: "" });
      setIsRendering(false);
      return undefined;
    }

    const cachedPreviewUrl = getCachedTemplatePreview(previewKey);
    if (cachedPreviewUrl) {
      canvasHostRef.current.replaceChildren();
      setPreviewState({ key: previewKey, url: cachedPreviewUrl });
      setIsRendering(false);
      return undefined;
    }

    let isCancelled = false;
    const canvasElement = document.createElement("canvas");
    canvasHostRef.current.replaceChildren(canvasElement);
    setIsRendering(true);
    setPreviewState({ key: previewKey, url: "" });

    const renderPreview = async () => {
      try {
        const nextPreviewUrl = await getOrRenderTemplatePreview({
          badgeSize: currentBadgeSize,
          cacheKey: previewKey,
          canvasElement,
          template: currentTemplate,
        });

        if (!isCancelled) {
          setPreviewState({ key: previewKey, url: nextPreviewUrl });
        }
      } catch (error) {
        console.error("Could not render event template preview.", error);
      } finally {
        if (!isCancelled) {
          setIsRendering(false);
        }
      }
    };

    renderPreview();

    return () => {
      isCancelled = true;
      canvasHostRef.current?.replaceChildren();
    };
  }, [previewKey]);

  return (
    <div className="event-card-preview" aria-hidden="true">
      {canRender && (isRendering || !previewUrl) && (
        <div className="event-card-preview-shimmer" />
      )}
      {previewUrl && (
        <img
          alt=""
          className="event-card-preview-image"
          draggable="false"
          src={previewUrl}
          title={title}
        />
      )}
      <div className="event-card-preview-canvas" ref={canvasHostRef} />
    </div>
  );
};

export default EventTemplatePreview;
